/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-unused-expressions */
import React, { ReactElement, useEffect } from 'react';
import { useTable, usePagination, Column } from 'react-table';

import {
  Table as ChakraTable,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Select,
  Flex,
  Text,
  IconButton,
  HStack,
  useColorModeValue,
  Spinner,
  Skeleton,
} from '@chakra-ui/react';

import {
  LuChevronsLeft,
  LuChevronLeft,
  LuChevronsRight,
  LuChevronRight,
} from 'react-icons/lu';

export interface ITableDataProps {
  [key: string]: string | number | boolean | Date | ReactElement | null;
}

interface ITableProps {
  columns: Array<Column<ITableDataProps>>;
  data: Array<ITableDataProps>;

  pageIndex?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeControl?: number;
  tableLength?: number;

  pageSizeOptions?: number[];
  onRowClick?: (...args: any[]) => any;
  hideFooter?: boolean;
  isLoading?: boolean;
}

const Table: React.FC<ITableProps> = ({
  columns,
  data,
  pageIndex,
  pageSizeControl,
  onPageChange,
  onPageSizeChange,
  tableLength,
  onRowClick,
  hideFooter,
  pageSizeOptions,
  isLoading,
}: ITableProps) => {
  const isServerSide =
    typeof pageIndex === 'number' &&
    typeof pageSizeControl === 'number' &&
    typeof tableLength === 'number';

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex: actualPage, pageSize },
  } = useTable(
    {
      columns,
      data: React.useMemo(() => data, [data]),
      manualPagination: isServerSide,
      pageCount: isServerSide
        ? Math.ceil((tableLength ?? 0) / pageSizeControl!)
        : undefined,
      initialState: {
        pageIndex: pageIndex ?? 0,
        pageSize: pageSizeControl ?? 10,
      },
      autoResetPage: !isServerSide,
    },
    usePagination,
  );

  const color = useColorModeValue('gray.700', 'white');
  const hoverBg = useColorModeValue('gray.100', 'gray.900');

  const pageSizeOpts = pageSizeOptions || [10, 20, 30, 40, 50];

  useEffect(() => {
    if (isServerSide && typeof pageIndex === 'number') {
      gotoPage(pageIndex);
    }
  }, [isServerSide, pageIndex, gotoPage]);

  useEffect(() => {
    if (isServerSide && typeof pageSizeControl === 'number') {
      setPageSize(pageSizeControl);
    }
  }, [isServerSide, pageSizeControl, setPageSize]);

  const currentPage = isServerSide ? pageIndex! : actualPage;

  const canGoPrevious = isServerSide ? currentPage > 0 : canPreviousPage;

  const canGoNext = isServerSide ? currentPage < pageCount - 1 : canNextPage;

  return (
    <Flex direction="column" height="100%" justify="space-between">
      <ChakraTable variant="simple" size="sm" {...getTableProps()}>
        <Thead h="6vh">
          {headerGroups?.map(headerGroup => (
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <Th
                  textAlign={column.id === 'actions' ? 'center' : 'inherit'}
                  {...(column.getHeaderProps?.() ?? {})}
                >
                  {column.render('Header')}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {/* Pra cada item da pagina */}
          {page?.map((row: any) => {
            prepareRow(row);
            return (
              <Tr
                {...row.getRowProps()}
                cursor={onRowClick ? 'pointer' : 'default'}
                _hover={{ backgroundColor: onRowClick ? hoverBg : 'none' }}
                onClick={() => onRowClick?.(row) ?? undefined}
              >
                {/* pra cada coluna do item */}
                {row?.cells?.map((cell: any) => {
                  return (
                    <Td p="1rem" {...cell.getCellProps()} color={color}>
                      {cell.render('Cell')}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
          {isLoading && (
            <Tr>
              <Td colSpan={columns.length}>
                <Skeleton height="35px" w="100%" my="2" />
                <Skeleton height="35px" w="100%" my="2" />
                <Skeleton height="35px" w="100%" my="2" />
              </Td>
            </Tr>
          )}
        </Tbody>
      </ChakraTable>

      <Flex
        mt="auto"
        justify="space-between"
        align="center"
        m={4}
        hidden={hideFooter}
      >
        <HStack>
          <Text>Total de Itens: {tableLength ?? data?.length}</Text>
        </HStack>
        <HStack>
          <Text>Linhas por página:</Text>
          <Select
            w={20}
            value={pageSize}
            onChange={e => {
              const size = Number(e.target.value);
              isServerSide ? onPageSizeChange?.(size) : setPageSize(size);
            }}
            size="md"
            rounded="2xl"
            color={color}
          >
            {pageSizeOpts?.map(pageSize => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </Select>
          <Text>
            {(pageIndex ?? actualPage) + 1} de {pageCount}
          </Text>
          <IconButton
            variant="ghost"
            aria-label="Chevron Double Left"
            icon={<LuChevronsLeft size={22} />}
            onClick={() => (isServerSide ? onPageChange?.(0) : gotoPage(0))}
            disabled={!canGoPrevious}
          />

          <IconButton
            variant="ghost"
            aria-label="Arrow Left"
            icon={<LuChevronLeft size={22} />}
            onClick={() =>
              isServerSide ? onPageChange?.(currentPage - 1) : previousPage()
            }
            disabled={!canGoPrevious}
          />

          <IconButton
            variant="ghost"
            aria-label="Arrow Right"
            icon={<LuChevronRight size={22} />}
            onClick={() =>
              isServerSide ? onPageChange?.(currentPage + 1) : nextPage()
            }
            disabled={!canGoNext}
          />

          <IconButton
            variant="ghost"
            aria-label="Chevron Double Right"
            icon={<LuChevronsRight size={22} />}
            onClick={() =>
              isServerSide
                ? onPageChange?.(pageCount - 1)
                : gotoPage(pageCount - 1)
            }
            disabled={!canGoNext}
          />
        </HStack>
      </Flex>
    </Flex>
  );
};

export default Table;
